// iiif-tiled-viewer.component.ts
import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef, HostListener, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { LanguageService } from 'src/app/services/language.service';

interface TileInfo {
  x: number;
  y: number;
  width: number;
  height: number;
  url: string;
  loaded: boolean;
  loading: boolean;
  error: boolean;
  col: number;
  row: number;
}

interface SingleImageSetting {
  visible: boolean;
  opacity: number;
  normalizedOpacity?: number;
}

interface ImageType {
  id: string;
  name: string;
  ariaLabel: string;
  baseUrl: string;
  previewUrl: string;
  tiles: TileInfo[];
  settings: SingleImageSetting;
}

export interface ImageInput {
  id: string;
  name: string;
  baseUrl: string;
}

@Component({
  selector: 'app-iiif-tiled-viewer',
  templateUrl: './iiif-tiled-viewer.component.html',
  styleUrls: ['./iiif-tiled-viewer.component.scss'],
})
export class IIIFTiledViewerComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() containerWidth: number = 800;
  @Input() containerHeight: number = 600;
  @Input() alt: string = 'IIIF Image';
  @Input() images: ImageInput[] = [];
  @Input() imageWidth: number = 7216;
  @Input() imageHeight: number = 5412;
  @Input() fitMode: 'fill-width' | 'fill-container' | 'fit-image' | 'maintain-aspect' = 'maintain-aspect';
  
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

  loading: boolean = false;
  error: boolean = false;
  
  previewLoaded: Map<string, boolean> = new Map();
  
  zoomLevel: number = 1;
  minZoom: number = 0.1;
  maxZoom: number = 5;
  zoomStep: number = 0.25;
  offsetX: number = 0;
  offsetY: number = 0;
  
  rotation: number = 0;
  rotationStep: number = 90;
  
  isDragging: boolean = false;
  dragStartX: number = 0;
  dragStartY: number = 0;
  dragStartOffsetX: number = 0;
  dragStartOffsetY: number = 0;
  
  displayWidth: number = 0;
  displayHeight: number = 0;
  displayScale: number = 1;
  
  imageTypes: ImageType[] = [];
  currentImageType: string = '';
  
  showSettings: boolean = false;
  currentLanguage: 'he' | 'en' = 'he';
  
  // Add flag to track if user has interacted
  hasUserInteracted: boolean = false;

  constructor(private cdr: ChangeDetectorRef, private languageService: LanguageService) {}

  ngOnInit() {
    this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang as 'he' | 'en';
      if (this.images.length > 0) {
      this.initializeImageTypes();
      // Don't load image here - wait for container to be ready
    }
    });
  }

  ngAfterViewInit() {
    // Get actual container dimensions after view is initialized
    this.updateContainerDimensions();
    if (this.images.length > 0) {
      this.loadImage();
    }
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['images'] && !changes['images'].firstChange) {
      this.initializeImageTypes();
      this.loadImage();
    }
  }

  ngOnDestroy() {
    this.previewLoaded.clear();
  }

  getImageTypeNameTranslated(image: ImageInput): string {
    switch (image.id) {
      case 'color':
        return 'צבע';
      case 'infrared':
        return 'אינפרה אדום';
      case 'raking_left':
        return 'תאורה חוצית שמאלית ';
      case 'raking_right':
        return 'תאורה חוצית ימנית ';
      default:
        return image.name;
    }
  }

  private initializeImageTypes() {
    this.imageTypes = this.images.map((image, index) => ({
      id: image.id,
      name: this.currentLanguage === 'he' ? this.getImageTypeNameTranslated(image) : image.name,
      ariaLabel: this.currentLanguage === 'he' ? this.getImageTypeNameTranslated(image) : image.name,
      baseUrl: image.baseUrl,
      // Use full resolution for preview - we'll scale it manually to match tiles exactly
      previewUrl: `${image.baseUrl}/full/full/0/default.jpg`,
      tiles: [],
      settings: {
        visible: index === 0,
        opacity: 1,
        normalizedOpacity: index === 0 ? 1 : 0
      }
    }));
    console.log(this.imageTypes[0].baseUrl, this.imageTypes[0].previewUrl);
    if (this.imageTypes.length > 0) {
      this.currentImageType = this.imageTypes[0].id;
    }
    this.cdr.detectChanges();
  }

  private loadImage() {
    this.loading = true;
    this.error = false;
    this.previewLoaded.clear();
    
    try {
      const scaleX = this.containerWidth / this.imageWidth;
      const scaleY = this.containerHeight / this.imageHeight;
      
      let scale: number;
      
      switch (this.fitMode) {
        case 'fill-width':
          // Fill container width completely (may stretch or exceed height)
          scale = scaleX;
          break;
          
        case 'fill-container':
          // Fill entire container (may stretch image)
          scale = Math.max(scaleX, scaleY);
          break;
          
        case 'fit-image':
          // Container adapts to image at natural size
          scale = 1;
          this.containerHeight = this.imageHeight;
          break;
          
        case 'maintain-aspect':
        default:
          // Maintain aspect ratio, fit within container
          scale = Math.min(scaleX, scaleY);
          break;
      }
      
      // Set display dimensions 
      this.displayWidth = Math.round(this.imageWidth * scale);
      this.displayHeight = Math.round(this.imageHeight * scale);
      this.displayScale = scale;
      
      // If fit-image mode, update container to match image
      if (this.fitMode === 'fit-image') {
        this.containerWidth = this.displayWidth;
        this.containerHeight = this.displayHeight;
      }
      
      for (let imageType of this.imageTypes) {
        this.createTilesForType(imageType);
      }
      
      this.resetView();
      this.loading = false;
      
    } catch (err) {
      console.error('Failed to load IIIF images:', err);
      this.error = true;
      this.loading = false;
    }
    
    this.cdr.detectChanges();
  }

  private createTilesForType(imageType: ImageType) {
    imageType.tiles = [];
    
    const manifestTileInfo = this.getManifestTileInfo();
    const optimizedScaleFactor = this.calculateOptimizedScaleFactor();
    
    const tileWidth = Math.floor(manifestTileInfo.width / optimizedScaleFactor);
    const tileHeight = Math.floor(manifestTileInfo.height / optimizedScaleFactor);
    
    console.log(`Using tile size: ${tileWidth}x${tileHeight} (scale factor: ${optimizedScaleFactor})`);
    
    const imageBoundingBox = {
      x: 0,
      y: 0,
      width: this.imageWidth,
      height: this.imageHeight
    };
    
    const endX = imageBoundingBox.x + imageBoundingBox.width;
    const endY = imageBoundingBox.y + imageBoundingBox.height;
    
    let row = 0;
    let totalCols = 0;
    
    for (let y = imageBoundingBox.y; y < endY; y += tileHeight) {
      const currentTileHeight = Math.min(tileHeight, endY - y);
      
      let col = 0;
      for (let x = imageBoundingBox.x; x < endX; x += tileWidth) {
        const currentTileWidth = Math.min(tileWidth, endX - x);
        
        if (currentTileWidth > 0 && currentTileHeight > 0) {
          const region = `${x},${y},${currentTileWidth},${currentTileHeight}`;
          const url = `${imageType.baseUrl}/${region}/full/0/default.jpg`;
          
          imageType.tiles.push({
            col,
            row,
            x: Math.round(x * this.displayScale),
            y: Math.round(y * this.displayScale),
            width: Math.round(currentTileWidth * this.displayScale),
            height: Math.round(currentTileHeight * this.displayScale),
            url,
            loaded: false,
            loading: true,
            error: false
          });
        }
        col++;
      }
      if (row === 0) {
        totalCols = col; // Capture the number of columns from the first row
      }
      row++;
    }
    
    console.log(`Created ${imageType.tiles.length} tiles in ${totalCols}x${row} grid`);
  }

  private getManifestTileInfo(): { width: number; height: number; scaleFactors: number[] } {
    // In a real implementation, this would come from the IIIF manifest
    // For now, return default tile information based on common IIIF standards
    return {
      width: 1024,    // Default tile width
      height: 1024,   // Default tile height
      scaleFactors: [1, 2, 4, 8, 16]
    };
  }

  private calculateOptimizedScaleFactor(): number {
    const manifestTileInfo = this.getManifestTileInfo();
    
    // Calculate the scale factor based on display size
    // We want tiles that are appropriately sized for the current display
    const displayPixelRatio = Math.max(
      this.displayWidth / this.imageWidth,
      this.displayHeight / this.imageHeight
    );
    
    // Find the best scale factor from available options
    // We want the smallest scale factor that still provides enough detail
    const targetTileDisplaySize = 256; // Target tile size in display pixels
    const currentTileDisplaySize = manifestTileInfo.width * this.displayScale;
    
    let bestScaleFactor = 1;
    for (const scaleFactor of manifestTileInfo.scaleFactors) {
      const scaledTileSize = currentTileDisplaySize / scaleFactor;
      if (scaledTileSize >= targetTileDisplaySize) {
        bestScaleFactor = scaleFactor;
      } else {
        break; // Scale factors should be in ascending order
      }
    }
    
    // Ensure we don't go too small or too large
    const minScaleFactor = Math.min(...manifestTileInfo.scaleFactors);
    const maxScaleFactor = Math.max(...manifestTileInfo.scaleFactors);
    
    bestScaleFactor = Math.max(minScaleFactor, Math.min(maxScaleFactor, bestScaleFactor));
    
    console.log(`Display scale: ${this.displayScale}, Target tile display size: ${targetTileDisplaySize}px, Selected scale factor: ${bestScaleFactor}`);
    
    return bestScaleFactor;
  }

  switchToImageType(typeId: string) {
    const index = this.imageTypes.findIndex(t => t.id === typeId);
    if (index !== -1) {
      this.currentImageType = typeId;
      
      this.imageTypes.forEach((type, i) => {
        type.settings.visible = i === index;
        type.settings.opacity = 1;
      });
      
      this.onImageSettingChanged();
    }
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
  }

  onVisibilityChanged(imageType: ImageType) {
    this.onImageSettingChanged();
  }

  onOpacityChanged(imageType: ImageType, event: Event) {
    const target = event.target as HTMLInputElement;
    if (target) {
      const oldOpacity = imageType.settings.opacity;
      imageType.settings.opacity = parseFloat(target.value);
      
      console.log(`Opacity changed for ${imageType.name}:`, {
        old: oldOpacity,
        new: imageType.settings.opacity,
        normalizedBefore: imageType.settings.normalizedOpacity
      });
      
      if (!imageType.settings.visible && imageType.settings.opacity > 0) {
        imageType.settings.visible = true;
      }
      
      this.onImageSettingChanged();
      
      console.log('After normalization:', {
        opacity: imageType.settings.opacity,
        normalized: imageType.settings.normalizedOpacity,
        visible: imageType.settings.visible
      });
    }
}

  onSliderKeydown(imageType: ImageType, event: KeyboardEvent) {
    const step = 0.05;
    let newValue = imageType.settings.opacity;
    
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = Math.max(0, imageType.settings.opacity - step);
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = Math.min(1, imageType.settings.opacity + step);
        event.preventDefault();
        break;
      case 'Home':
        newValue = 0;
        event.preventDefault();
        break;
      case 'End':
        newValue = 1;
        event.preventDefault();
        break;
      case 'PageDown':
        newValue = Math.max(0, imageType.settings.opacity - 0.1);
        event.preventDefault();
        break;
      case 'PageUp':
        newValue = Math.min(1, imageType.settings.opacity + 0.1);
        event.preventDefault();
        break;
      default:
        return; // Don't update if no relevant key was pressed
    }
    
    // Update the opacity
    imageType.settings.opacity = newValue;
    
    // Create a synthetic event for consistency with the input handler
    const syntheticEvent = {
      target: { value: newValue.toString() }
    } as any;
    this.onOpacityChanged(imageType, syntheticEvent);
  }

  private onImageSettingChanged() {
    this.normalizeOpacity();
    this.cdr.detectChanges();
  }

  private normalizeOpacity() {
    const visibleImages = this.imageTypes.filter(t => t.settings.visible);
    
    visibleImages.forEach(imageType => {
      imageType.settings.normalizedOpacity = imageType.settings.opacity;
    });
  }

  getVisibleImageTypes(): ImageType[] {
    return this.imageTypes.filter(t => t.settings.visible);
  }

  isImageType(imageType: ImageType, type: string): boolean {
    const id = (imageType.id || imageType.name || '').toLowerCase();
    
    switch (type) {
      case 'color': return id.includes('color');
      case 'infrared': return id.includes('infrared') || id.includes('ir');
      case 'raking-left': return id.includes('raking') && id.includes('left');
      case 'raking-right': return id.includes('raking') && id.includes('right');
      default: return false;
    }
  }



  getImageByType(type: string): ImageType | null {
    return this.imageTypes.find(imageType => this.isImageType(imageType, type)) || null;
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.preventDefault();
    
    const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep;
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
    
    if (newZoom !== this.zoomLevel) {
      this.hasUserInteracted = true;
      const rect = this.container.nativeElement.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      this.zoomToPoint(newZoom, mouseX, mouseY);
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (event.button === 0) {
      this.startDrag(event.clientX, event.clientY);
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.drag(event.clientX, event.clientY);
    }
  }

  @HostListener('mouseup')
  @HostListener('mouseleave')
  onMouseUp() {
    this.endDrag();
  }

  private startDrag(x: number, y: number) {
    this.isDragging = true;
    this.dragStartX = x;
    this.dragStartY = y;
    this.dragStartOffsetX = this.offsetX;
    this.dragStartOffsetY = this.offsetY;
  }

  private drag(x: number, y: number) {
    const deltaX = x - this.dragStartX;
    const deltaY = y - this.dragStartY;
    
    this.hasUserInteracted = true;
    this.offsetX = this.dragStartOffsetX + deltaX;
    this.offsetY = this.dragStartOffsetY + deltaY;
    
    this.constrainOffset();
  }

  private endDrag() {
    this.isDragging = false;
  }

  private zoomToPoint(newZoom: number, pointX: number, pointY: number) {
    const imageX = (pointX - this.containerWidth / 2 - this.offsetX) / this.zoomLevel;
    const imageY = (pointY - this.containerHeight / 2 - this.offsetY) / this.zoomLevel;
    
    this.zoomLevel = newZoom;
    
    this.offsetX = pointX - this.containerWidth / 2 - imageX * this.zoomLevel;
    this.offsetY = pointY - this.containerHeight / 2 - imageY * this.zoomLevel;
    
    this.constrainOffset();
  }

  private constrainOffset() {
    const scaledWidth = this.displayWidth * this.zoomLevel;
    const scaledHeight = this.displayHeight * this.zoomLevel;
    
    const maxOffsetX = Math.max(0, (scaledWidth - this.containerWidth) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - this.containerHeight) / 2);
    
    this.offsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, this.offsetX));
    this.offsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, this.offsetY));
  }

  get imageTransform(): string {
    // Calculate centering offset for the scaled image within the container
    const centerOffsetX = (this.containerWidth - this.displayWidth) / 2;
    const centerOffsetY = (this.containerHeight - this.displayHeight) / 2;
    
    const needsTranslation = this.hasUserInteracted || this.zoomLevel !== 1 || this.rotation !== 0;
    
    if (!needsTranslation) {
      // For initial state, center the image in the container
      return `translate(${centerOffsetX}px, ${centerOffsetY}px) rotate(${this.rotation}deg) scale(${this.zoomLevel})`;
    }
    
    // Apply transform with offset for user interactions, keeping the centering
    return `translate(${centerOffsetX + this.offsetX}px, ${centerOffsetY + this.offsetY}px) rotate(${this.rotation}deg) scale(${this.zoomLevel})`;
  }

  get transformOrigin(): string {
    // Use the center of the actual image, not the container
    return `${this.displayWidth / 2}px ${this.displayHeight / 2}px`;
  }

  onTileLoad(tile: TileInfo) {
    tile.loaded = true;
    tile.loading = false;
  }

  onTileError(tile: TileInfo) {
    tile.error = true;
    tile.loading = false;
  }
  
  onPreviewLoad(imageTypeId: string) {
    this.previewLoaded.set(imageTypeId, true);
  }
  
  onPreviewError(imageTypeId: string) {
    this.previewLoaded.set(imageTypeId, false);
  }

  zoomIn() {
    this.hasUserInteracted = true;
    const newZoom = Math.min(this.maxZoom, this.zoomLevel + this.zoomStep);
    this.zoomToPoint(newZoom, this.containerWidth / 2, this.containerHeight / 2);
  }

  zoomOut() {
    this.hasUserInteracted = true;
    const newZoom = Math.max(this.minZoom, this.zoomLevel - this.zoomStep);
    this.zoomToPoint(newZoom, this.containerWidth / 2, this.containerHeight / 2);
  }

  rotateLeft() {
    this.hasUserInteracted = true;
    this.rotation = (this.rotation - this.rotationStep + 360) % 360;
  }

  rotateRight() {
    this.hasUserInteracted = true;
    this.rotation = (this.rotation + this.rotationStep) % 360;
  }

  resetView() {
    this.zoomLevel = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.rotation = 0;
    this.hasUserInteracted = false;
  }

  reload() {
    this.imageTypes.forEach(type => type.tiles = []);
    this.loadImage();
  }

  trackTile(index: number, tile: TileInfo): string {
    return `${tile.col}-${tile.row}`;
  }

  get canZoomIn(): boolean {
    return this.zoomLevel < this.maxZoom;
  }

  get canZoomOut(): boolean {
    return this.zoomLevel > this.minZoom;
  }

  get loadedTilesCount(): number {
    return this.imageTypes.reduce((total, type) => {
      if (type.settings.visible) {
        return total + type.tiles.filter(t => t.loaded).length;
      }
      return total;
    }, 0);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    // Update dimensions on window resize
    this.updateContainerDimensions();
    if (this.images.length > 0) {
      this.loadImage();
    }
  }

  private updateContainerDimensions() {
    if (this.container && this.container.nativeElement) {
      const rect = this.container.nativeElement.getBoundingClientRect();
      this.containerWidth = rect.width;
      // containerHeight remains as input since it's set explicitly
    }
  }
}